import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Header from "../../components/layout/Header";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useSubscription } from "../../providers/SubscriptionProvider";
import { StripeService } from "../../services/stripeService";
import { HiChevronLeft, HiSparkles } from "react-icons/hi2";
import StripeSubscriptionActions from "./StripeSubscriptionActions";
import { COUNTRY_CURRENCY_MAP } from "../../shared/countryCurrencyMap";
import { analyticsService } from "../../services/analyticsService";

// ─── Included features per product type (only what's included) ────────────────
const AI_FEATURES = new Set([
  "Personalised AI Insights & Analysis",
  "The Wizard AI",
  "Custom Insights & Wizard Tone",
]);

const INCLUDED_FEATURES: Record<string, string[]> = {
  free: [
    "1 Wearable Device",
    "Wellness Questionnaire",
    "Training Load & Exercise Logs",
    "Recovery, Niggles & Notes",
    "Planner & Scheduled Sessions",
    "Female Cycle Logging",
    "Last 7 Days Analytics",
  ],
  plus: [
    "Peak Score & Peak Readiness",
    "2 Wearable Devices",
    "Wellness Questionnaire",
    "Training Load & Exercise Logs",
    "Recovery, Niggles & Notes",
    "Load Balance",
    "Heart Lung Recovery Index (HLR)",
    "Activity & Exercise Analytics",
    "Saved Session Templates",
    "Export Data to CSV",
    "Performance Goals",
    "Custom Dashboard Background",
  ],
  premium: [
    "Everything in Plus",
    "3 Wearable Devices",
    "Weekly, Monthly, 12 Week & Custom Date Range Analytics",
    "Personalised AI Insights & Analysis",
    "The Wizard AI",
    "Custom Insights & Wizard Tone",
  ],
};

// ─── Helpers — detect product type AND billing period from a priceId ─
// getBillingPeriodForPriceId in the service only knows Premium + legacy IDs.
// We extend it here to also cover Plus IDs, then derive product type from that.

const ALL_REGIONS = ["US", "CA", "AU", "ZA", "EU", "GB", "IN"] as const;
const BILLING_PERIODS = ["month", "quarter", "year"] as const;

// Returns "plus" | "premium" | null by exhaustively checking all known price IDs.
const detectCurrentProductType = (
  priceId: string | null | undefined,
): "plus" | "premium" | null => {
  if (!priceId) return null;

  // Check all Plus price IDs first
  for (const region of ALL_REGIONS) {
    for (const period of BILLING_PERIODS) {
      const plusId = StripeService.getPlusPriceIdForCountry(
        regionToCountry[region],
        period,
      );
      if (plusId === priceId) return "plus";
    }
  }

  // Fall back to service method for Premium + legacy IDs
  const billingPeriod = StripeService.getBillingPeriodForPriceId(priceId);
  if (billingPeriod) return "premium";

  return null;
};

// Returns the billing period for any known price ID — Plus, Premium, or legacy.
// Extends the service's getBillingPeriodForPriceId which only covers Premium.
const getBillingPeriodForAnyPriceId = (
  priceId: string | null | undefined,
): "month" | "quarter" | "year" | null => {
  if (!priceId) return null;

  // Check Plus price IDs across all regions and periods
  for (const region of ALL_REGIONS) {
    for (const period of BILLING_PERIODS) {
      const plusId = StripeService.getPlusPriceIdForCountry(
        regionToCountry[region],
        period,
      );
      if (plusId === priceId) return period;
    }
  }

  // Fall back to service method for Premium + legacy
  return StripeService.getBillingPeriodForPriceId(priceId) as
    | "month"
    | "quarter"
    | "year"
    | null;
};

// Representative country per region (for the Plus price ID lookup above)
const regionToCountry: Record<string, string> = {
  US: "United States",
  CA: "Canada",
  AU: "Australia",
  ZA: "South Africa",
  EU: "Germany",
  GB: "United Kingdom",
  IN: "India",
};

// ─── CHANGE 3: Card style config per product type ────────────────────────────
const CARD_STYLE: Record<
  string,
  { gradient: string; accentText: string; accentBg: string; title: string }
> = {
  plus: {
    gradient: "bg-base-100",
    accentText: "text-base-content",
    accentBg: "bg-white/20 text-base-content",
    title: "Plus",
  },
  premium: {
    gradient: "bg-base-100",
    accentText: "text-base-content",
    accentBg: "bg-white/20 text-base-content",
    title: "Premium",
  },
};

const SubscriptionSettings: React.FC = () => {
  const { currentUser, userData } = useAuth();
  const [userCurrency, setUserCurrency] = useState<string>("USD");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stripeProducts, setStripeProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncingSubscription, setSyncingSubscription] = useState(false);
  const [downgrading, setDowngrading] = useState(false); // NEW: track downgrade loading
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    variant: "danger" | "default";
    onConfirm: () => void;
  } | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<
    "quarter" | "year" | "month"
  >("quarter");
  const [validatedCountry, setValidatedCountry] =
    useState<string>("United States");

  const [countryValidated, setCountryValidated] = useState(false);

  // New simplified USD-only pricing (PEAK account) — local to this section.
  const [peakBillingPeriod, setPeakBillingPeriod] = useState<"month" | "year">(
    "month",
  );
  const [peakActionLoading, setPeakActionLoading] = useState<string | null>(
    null,
  );

  const { subscription: userSubscription, refreshSubscription } =
    useSubscription();

  // Handle Stripe redirect query parameters — UNCHANGED
  useEffect(() => {
    const upgradeSuccess = searchParams.get("upgrade_success");
    const upgradeCanceled = searchParams.get("upgrade_canceled");

    if (upgradeSuccess === "true") {
      setSearchParams({});
      setSyncingSubscription(true);

      if (currentUser?.uid) {
        const functions = getFunctions();
        const triggerCheckUserSubscription = httpsCallable(
          functions,
          "triggerCheckUserSubscription",
        );

        triggerCheckUserSubscription({ userId: currentUser.uid })
          .then(async (result: any) => {
            await refreshSubscription();
            const subscriptionData = result.data?.subscription;
            analyticsService.trackPurchase({
              productName: subscriptionData?.subscriptionType || "Premium",
              priceId: subscriptionData?.stripeDetails?.priceId,
              value: subscriptionData?.stripeDetails?.amount,
              currency:
                subscriptionData?.stripeDetails?.currency || userCurrency,
              billingPeriod: subscriptionData?.stripeDetails?.billingPeriod,
              userId: currentUser.uid,
            });
          })
          .catch(console.error)
          .finally(() => {
            setSyncingSubscription(false);
          });
      } else {
        setSyncingSubscription(false);
      }
    } else if (upgradeCanceled === "true") {
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, currentUser?.uid, userCurrency]);

  // Country validation — UNCHANGED
  useEffect(() => {
    const validateCountry = async () => {
      const validated = await StripeService.validateCountryForPricing(
        userData?.country,
      );
      setValidatedCountry(validated);
      setCountryValidated(true);
      const detectedCurrency = COUNTRY_CURRENCY_MAP[validated] || "USD";
      setUserCurrency(detectedCurrency);
    };
    validateCountry();
  }, [userData?.country]);

  useEffect(() => {
    // Don't fetch if country isn't set yet
    if (!validatedCountry) return;

    const fetchStripeProducts = async () => {
      try {
        setLoadingProducts(true);
        const products = await StripeService.getProducts();
        // console.log("products received f rom service ", products);
        setStripeProducts(products);
      } catch (err) {
        console.error("Error fetching Stripe products:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchStripeProducts();
  }, [validatedCountry, userCurrency]);

  // Fetch subscription — UNCHANGED
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        await refreshSubscription();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to fetch subscription data",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchSubscription();
  }, [currentUser?.uid]);

  const getCurrentSubscriptionType = () =>
    userSubscription?.subscriptionType?.toLowerCase();

  const normalizeDuration = (duration: string) =>
    StripeService.normalizeDuration(duration);

  const formatDuration = (duration: string) => {
    const normalized = normalizeDuration(duration);
    switch (normalized?.toLowerCase()) {
      case "quarter":
        return "3 months";
      case "year":
        return "year";
      case "month":
        return "month";
      default:
        return normalized;
    }
  };

  const formatPrice = (price: number, currency: string) => {
    const currencySymbols: Record<string, string> = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      AUD: "$",
      CAD: "$",
      INR: "₹",
      ZAR: "R",
    };
    const symbol = currencySymbols[currency] || currency + " ";
    if ((currency === "INR" || currency === "ZAR") && price >= 1000) {
      return `${symbol}${price.toLocaleString("en-IN")}`;
    }
    return `${symbol}${price.toFixed(2)}`;
  };

  // ─── Downgrade to Basic (Prompt + Execute) ────────────────────────────────
  const promptDowngradeToBasic = () => {
    if (!currentUser) return;

    const subscriptionId = userSubscription?.stripeDetails?.subscriptionId;
    if (!subscriptionId) {
      alert("No active subscription found.");
      return;
    }

    const isTrialing =
      userSubscription?.stripeDetails?.subscriptionStatus === "trialing";
    const currentPlanName = currentProductType
      ? currentProductType.charAt(0).toUpperCase() + currentProductType.slice(1)
      : "paid";

    const message = isTrialing
      ? `You're currently on a ${currentPlanName} free trial.\n\nDowngrading to Basic will cancel your trial immediately and you'll lose access to ${currentPlanName} features right away.`
      : `You're currently on the ${currentPlanName} plan.\n\nYour subscription will be cancelled at the end of your current billing period. You'll keep access to ${currentPlanName} features until then.`;

    setConfirmModal({
      title: "Downgrade to Basic?",
      message,
      confirmLabel: "Downgrade",
      variant: "danger",
      onConfirm: executeDowngradeToBasic,
    });
  };

  const executeDowngradeToBasic = async () => {
    setConfirmModal(null);
    try {
      if (!currentUser) throw new Error("User not authenticated.");

      const subscriptionId = userSubscription?.stripeDetails?.subscriptionId;
      if (!subscriptionId) throw new Error("No active subscription found.");

      const isTrialing =
        userSubscription?.stripeDetails?.subscriptionStatus === "trialing";

      setDowngrading(true);

      if (isTrialing) {
        const success = await StripeService.cancelSubscription(
          subscriptionId,
          currentUser.uid,
        );
        if (!success) throw new Error("Failed to cancel subscription.");
      } else {
        // ✅ FIX: Use StripeService instead of httpsCallable
        const success = await StripeService.cancelAtPeriodEnd(
          subscriptionId,
          currentUser.uid,
        );
        if (!success)
          throw new Error("Failed to cancel subscription at period end.");
      }

      await refreshSubscription();
      window.location.reload();
    } catch (error) {
      alert(error instanceof Error ? error.message : "Something went wrong!");
    } finally {
      setDowngrading(false);
    }
  };

  // ─── New simplified USD pricing (PEAK account) ────────────────────────────
  // Fresh subscriber — straight checkout against the new account, no region lookup.
  const handleUpgradeToPeak = async (
    plan: "plus" | "premium",
    period: "month" | "year",
  ) => {
    if (!currentUser) return;
    const key = `${plan}-${period}`;
    setPeakActionLoading(key);
    try {
      const priceId = StripeService.getPeakPriceId(plan, period);
      const trialDays = userData?.hasUsedTrial ? 0 : 14;
      const successUrl = `${window.location.origin}/settings/subscription?upgrade_success=true`;
      const cancelUrl = `${window.location.origin}/settings/subscription?upgrade_canceled=true`;

      const sessionUrl = await StripeService.createCheckoutSession(
        priceId,
        "",
        successUrl,
        cancelUrl,
        currentUser.uid,
        plan,
        trialDays,
      );

      if (sessionUrl) window.location.href = sessionUrl;
      else throw new Error("Failed to start checkout.");
    } catch (error) {
      alert(error instanceof Error ? error.message : "Something went wrong!");
    } finally {
      setPeakActionLoading(null);
    }
  };

  // Existing legacy subscriber choosing to move to the new simplified pricing.
  // There is no in-place account-to-account update in Stripe: the legacy
  // subscription is left to run out its current period (no gap, no refund
  // needed) while a new Checkout session starts the new-account subscription.
  const promptSwitchToNewPricing = (
    plan: "plus" | "premium",
    period: "month" | "year",
  ) => {
    if (!currentUser) return;
    const existingSubscriptionId = userSubscription?.stripeDetails?.subscriptionId;
    if (!existingSubscriptionId) {
      alert("No active subscription found.");
      return;
    }

    const planName = plan === "plus" ? "Plus" : "Premium";
    const price = StripeService.getPeakDisplayPrice(plan, period);

    setConfirmModal({
      title: `Switch to new ${planName} pricing?`,
      message: `Your current plan stays active until the end of this billing period — no gap in access. You'll then move to the new ${planName} plan at ${formatPrice(price, "USD")} / ${period === "year" ? "year" : "month"}. You'll complete a short checkout for the new plan now.`,
      confirmLabel: "Switch Plan",
      variant: "default",
      onConfirm: () => executeSwitchToNewPricing(plan, period),
    });
  };

  const executeSwitchToNewPricing = async (
    plan: "plus" | "premium",
    period: "month" | "year",
  ) => {
    setConfirmModal(null);
    const key = `${plan}-${period}`;
    setPeakActionLoading(key);
    try {
      if (!currentUser) throw new Error("User not authenticated.");
      const existingSubscriptionId = userSubscription?.stripeDetails?.subscriptionId;
      if (!existingSubscriptionId) throw new Error("No active subscription found.");

      // The legacy subscription is left untouched until the new one is
      // confirmed paid (the webhook cancels it then) — so an abandoned or
      // failed checkout here leaves the current plan exactly as it was.
      const priceId = StripeService.getPeakPriceId(plan, period);
      const trialDays = userData?.hasUsedTrial ? 0 : 14;
      const successUrl = `${window.location.origin}/settings/subscription?upgrade_success=true`;
      const cancelUrl = `${window.location.origin}/settings/subscription?upgrade_canceled=true`;

      const sessionUrl = await StripeService.createCheckoutSession(
        priceId,
        "",
        successUrl,
        cancelUrl,
        currentUser.uid,
        plan,
        trialDays,
        existingSubscriptionId,
      );

      if (sessionUrl) window.location.href = sessionUrl;
      else throw new Error("Failed to start checkout for the new plan.");
    } catch (error) {
      alert(error instanceof Error ? error.message : "Something went wrong!");
    } finally {
      setPeakActionLoading(null);
    }
  };

  // Switching between Plus/Premium or billing period while already on the
  // new account — an in-place update (proration) against a peak price.
  const executeSwitchPeakPlan = async (plan: "plus" | "premium", period: "month" | "year") => {
    const key = `${plan}-${period}`;
    setPeakActionLoading(key);
    try {
      if (!currentUser) throw new Error("User not authenticated.");
      const existingSubscriptionId = userSubscription?.stripeDetails?.subscriptionId;
      if (!existingSubscriptionId) throw new Error("No active subscription found.");
      const isTrialing = userSubscription?.stripeDetails?.subscriptionStatus === "trialing";
      const newPriceId = StripeService.getPeakPriceId(plan, period);

      const success = await StripeService.updateSubscription(existingSubscriptionId, newPriceId, isTrialing);
      if (!success) throw new Error("Failed to update subscription");

      await refreshSubscription();
      window.location.reload();
    } catch (error) {
      alert(error instanceof Error ? error.message : "Something went wrong!");
    } finally {
      setPeakActionLoading(null);
    }
  };

  // Full-featured card for the new flat USD pricing — same look as the
  // legacy Plus/Premium cards (price box, Includes list), just driven by
  // peak pricing/account state instead of region lookups.
  const renderPeakCard = (plan: "plus" | "premium", period: "month" | "year") => {
    const style = CARD_STYLE[plan];
    const currency = "USD";
    const price = StripeService.getPeakDisplayPrice(plan, period);
    const pricePerMonth = period === "month" ? price : price / 12;
    const pricePerWeek = period === "month" ? price / 4.33 : price / 52;
    const key = `${plan}-${period}`;
    const isCurrentPeakPlan =
      isOnPeakAccount && currentSubscriptionType === plan && currentPeakPeriod === period;
    const isLoading = peakActionLoading === key;
    const onPeakOtherPlan = isOnPeakAccount && !isCurrentPeakPlan;

    let ctaButtonLabel: string;
    if (isCurrentPeakPlan) ctaButtonLabel = "Current Plan";
    else if (onPeakOtherPlan && currentSubscriptionType === plan) {
      ctaButtonLabel = `Switch to ${period === "year" ? "Annually" : "Monthly"}`;
    } else if (onPeakOtherPlan && plan === "premium") ctaButtonLabel = "Upgrade to Premium";
    else if (onPeakOtherPlan && plan === "plus") ctaButtonLabel = "Downgrade to Plus";
    else if (isStripeSubscriber) ctaButtonLabel = `Switch to ${style.title}`;
    else ctaButtonLabel = userData?.hasUsedTrial ? `Upgrade to ${style.title}` : "Start 14 Day Free Trial";

    return (
      <div
        key={plan}
        className={`${style.gradient} rounded-xl p-6 flex-1 flex flex-col relative`}
      >
        <h2 className="text-2xl font-bold font-geist text-base-content mb-1">
          {style.title}
        </h2>
        <p className="text-sm text-base-content/70 font-geist mb-3">
          {plan === "plus" ? "Train with clarity." : "Intelligent training tools."}
        </p>
        <div className="mb-1">
          <span className="text-4xl font-bold text-base-content font-grotesk">
            {formatPrice(price, currency)}
          </span>
          <span className="text-base-content/80 ml-2 text-sm">
            / {period === "year" ? "year" : "month"}
          </span>
        </div>
        <div className="flex gap-2 mb-4 text-xs mt-2">
          <div className="border border-base-content/20 px-4 py-2 pl-2 flex items-center font-geist rounded-md bg-base-content/5 text-base-content/60">
            <strong>{formatPrice(pricePerMonth, currency)}</strong>
            /month
          </div>
          <div className="border border-base-content/20 px-4 py-2 pl-2 flex items-center font-geist rounded-md bg-base-content/5 text-base-content/60">
            <strong>{formatPrice(pricePerWeek, currency)}</strong>
            /week
          </div>
        </div>
        <div className="mb-5 flex-1">
          <h3 className="text-base-content/60 text-xs font-medium uppercase tracking-wide mb-2">
            Includes
          </h3>
          <ul className="space-y-1.5">
            {INCLUDED_FEATURES[plan]?.map((feature, idx) => {
              const isAI = AI_FEATURES.has(feature);
              return (
                <li
                  key={idx}
                  className={`flex items-start gap-2 text-sm text-base-content/90 ${isAI ? "rounded-md px-2 -mx-2" : ""}`}
                >
                  <span className="mt-0.5 flex-shrink-0 text-base-content">
                    {isAI ? <HiSparkles className="h-4 w-4" /> : "✓"}
                  </span>
                  <span>{feature}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <button
          className={`w-full py-3 px-4 rounded-full font-bold font-grotesk transition-colors duration-200 flex items-center justify-center gap-2 ${
            isCurrentPeakPlan || isLoading
              ? "bg-base-300/50 text-base-content/60 cursor-default"
              : "bg-lime text-black hover:bg-lime/80"
          }`}
          disabled={isCurrentPeakPlan || isLoading}
          onClick={() => {
            if (isCurrentPeakPlan) return;
            if (onPeakOtherPlan) {
              executeSwitchPeakPlan(plan, period);
              return;
            }
            if (isStripeSubscriber) promptSwitchToNewPricing(plan, period);
            else handleUpgradeToPeak(plan, period);
          }}
        >
          {isLoading ? <span className="loading loading-spinner loading-xs" /> : ctaButtonLabel}
        </button>
      </div>
    );
  };

  const formatDate = (
    timestamp: { seconds: number; nanoseconds: number } | undefined,
  ) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="container max-w-5xl mx-auto py-8 px-4 min-h-[70vh] flex items-center justify-center">
        <div className="flex flex-col items-center justify-center">
          <div className="flex flex-col items-center gap-6">
            <h1 className="text-base-content text-[8rem] font-grotesk animate-pulse-custom relative z-10">
              ^
            </h1>
            <div className="text-center -mt-16">
              <p className="text-lg font-medium text-base-content font-grotesk">
                Loading subscription...
              </p>
              <p className="text-sm text-base-content/70 font-grotesk mt-2">
                Fetching your plan details
              </p>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <div className="w-32 h-1 bg-base-300 rounded-full overflow-hidden">
                <div className="h-full bg-base-200 rounded-full animate-pulse transition-all duration-1000 w-3/4"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const ctaLabel = userData?.hasUsedTrial ? "Upgrade to Premium" : "Bg-white";

  if (error) {
    return (
      <div className="container max-w-5xl mx-auto py-8 px-4">
        <h1 className="text-3xl font-semibold text-base-content mb-8">
          Subscription Settings
        </h1>
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 text-center">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Error Loading Subscription
          </h3>
          <p className="text-base-content/70 mb-6">{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const currentSubscriptionType = getCurrentSubscriptionType();

  // Detect which product type the user is currently on
  const currentProductType = detectCurrentProductType(
    userSubscription?.stripeDetails?.priceId,
  );
  const isStripeSubscriber = userSubscription?.subscriptionVia === "stripe";
  const isOnPeakAccount = userSubscription?.stripeDetails?.stripeAccount === "peak";
  // Old regional prices stay visible only for a Stripe subscriber still on
  // the original account. Everyone else sees the flat USD plans.
  const isLegacyStripeSubscriber = isStripeSubscriber && !isOnPeakAccount;
  const currentPeakPeriod = isOnPeakAccount
    ? StripeService.getBillingPeriodForPeakPriceId(
        userSubscription?.stripeDetails?.priceId,
      )
    : null;

  return (
    <div className="w-full pt-16">
      {/* ─── Custom Confirmation Modal ─── */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-base-100 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 border border-base-300">
            <h3 className="text-lg font-bold text-base-content mb-2 font-grotesk">
              {confirmModal.title}
            </h3>
            <p className="text-sm text-base-content/70 whitespace-pre-line leading-relaxed mb-6">
              {confirmModal.message}
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-5 py-2 pl-2.5 rounded-full text-sm font-semibold bg-base-200 text-base-content hover:bg-base-300 transition-colors"
                onClick={() => setConfirmModal(null)}
              >
                Cancel
              </button>
              <button
                className={`px-5 py-2 pl-2.5 rounded-full text-sm font-semibold transition-colors ${
                  confirmModal.variant === "danger"
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-lime text-black hover:bg-lime/80"
                }`}
                onClick={confirmModal.onConfirm}
              >
                {confirmModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
      {syncingSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-base-100 p-6 rounded-lg flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mb-4"></div>
            <p className="text-base-content font-medium">
              Activating your subscription...
            </p>
            <p className="text-base-content/60 text-sm mt-1">
              Please wait while we update your account
            </p>
          </div>
        </div>
      )}

      <Header />
      <div className="container max-w-5xl mx-auto py-8 px-8">
        <div className="flex items-center justify-between pb-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors"
            >
              <HiChevronLeft className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content ml-4">
              Subscription
            </h1>
          </div>
        </div>

        {/* Current Subscription Status Card — UNCHANGED */}
        <div className="mb-6 bg-base-100 rounded-lg border border-base-300 shadow-sm p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-medium text-base-content/60 uppercase tracking-wide">
                Current Plan
              </h2>
              <div className="mt-1 flex items-center gap-3">
                <span className="text-2xl font-bold text-base-content capitalize">
                  {userSubscription?.subscriptionType === "default" ||
                  !userSubscription?.subscriptionType
                    ? "Free"
                    : userSubscription.subscriptionType}
                </span>
                {userSubscription?.subscriptionVia &&
                  userSubscription.subscriptionVia !== "default" && (
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        userSubscription.subscriptionVia === "stripe"
                          ? "bg-base-300 text-base-content"
                          : userSubscription.subscriptionVia === "trial"
                            ? "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400"
                            : userSubscription.subscriptionVia === "admin"
                              ? "bg-purple-500/15 text-purple-700 dark:text-purple-400"
                              : "bg-base-200 text-base-content"
                      }`}
                    >
                      {userSubscription.subscriptionVia === "stripe"
                        ? "Current"
                        : userSubscription.subscriptionVia}
                    </span>
                  )}
              </div>
            </div>
          </div>
          {userSubscription?.subscriptionChangeMessage && (
            <p className="mt-3 text-sm text-base-content/70 italic border-t border-base-200 pt-3">
              {userSubscription.subscriptionChangeMessage}
            </p>
          )}
        </div>

        {/* Billing Period Toggle — legacy regional prices only */}
        {isLegacyStripeSubscriber && (
          <div className="flex justify-center mb-8">
            <fieldset aria-label="Payment frequency" className="w-full max-w-sm">
              <div className="flex bg-base-100 rounded-full p-1 text-center text-sm font-semibold shadow-inner">
                {[
                  { value: "month" as const, label: "Monthly" },
                  { value: "quarter" as const, label: "Quarterly" },
                  { value: "year" as const, label: "Annually", badge: true },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex-1 cursor-pointer rounded-full px-3 py-2 pl-2.5 transition-all duration-200 ${
                      billingPeriod === option.value
                        ? "bg-base-content text-base-100 shadow-md"
                        : "text-base-content/60 hover:text-base-content"
                    }`}
                  >
                    <input
                      type="radio"
                      name="billing-frequency"
                      value={option.value}
                      checked={billingPeriod === option.value}
                      onChange={() => setBillingPeriod(option.value)}
                      className="sr-only"
                    />
                    <span className="flex items-center justify-center gap-1">
                      {option.label}
                      {option.badge && (
                        <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-lime text-black">
                          -20%
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        )}

        {!isLegacyStripeSubscriber && (
          <div className="flex justify-center mb-8">
            <fieldset aria-label="Payment frequency" className="w-full max-w-sm">
              <div className="flex bg-base-100 rounded-full p-1 text-center text-sm font-semibold shadow-inner">
                {(["month", "year"] as const).map((period) => (
                  <label
                    key={period}
                    className={`flex-1 cursor-pointer rounded-full px-3 py-2 pl-2.5 text-center transition-all duration-200 ${
                      peakBillingPeriod === period
                        ? "bg-base-content text-base-100 shadow-md"
                        : "text-base-content/60 hover:text-base-content"
                    }`}
                  >
                    <input
                      type="radio"
                      name="peak-billing-frequency"
                      value={period}
                      checked={peakBillingPeriod === period}
                      onChange={() => setPeakBillingPeriod(period)}
                      className="sr-only"
                    />
                    <span>{period === "month" ? "Monthly" : "Annually"}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        )}

        {/* ─── Pricing Cards — Free, Plus & Premium ─── */}
        {/* ─── Pricing Cards — Basic, Plus & Premium ─── */}
        {loadingProducts && (
          <div className="flex flex-col items-center gap-4 mb-8">
            <p className="text-sm text-base-content/60 font-grotesk animate-pulse">
              Loading plans...
            </p>
            <div className="flex flex-col md:flex-row gap-4 w-full">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-base-100 rounded-xl p-6 flex-1 animate-pulse"
                >
                  <div className="h-8 bg-base-300 rounded w-24 mb-3"></div>
                  <div className="h-10 bg-base-300 rounded w-32 mb-4"></div>
                  <div className="space-y-2 mb-5">
                    <div className="h-4 bg-base-300 rounded w-full"></div>
                    <div className="h-4 bg-base-300 rounded w-3/4"></div>
                    <div className="h-4 bg-base-300 rounded w-5/6"></div>
                  </div>
                  <div className="h-12 bg-base-300 rounded-full w-full mt-auto"></div>
                </div>
              ))}
            </div>
          </div>
        )}
        {!loadingProducts && (
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            {/* ── Free / Basic Card ── */}
            <div className="bg-base-100 rounded-xl p-6 flex-1 flex flex-col">
              <h2 className="text-base-content/70 text-2xl font-bold font-geist mb-1">
                Basic
              </h2>
              <p className="text-sm text-base-content/70 font-geist mb-3">
                Start with the basics.
              </p>
              <div className="mb-1">
                <span className="text-3xl font-bold text-base-content font-grotesk">
                  Free
                </span>
              </div>
              <div className="inline-block bg-white/20 text-base-content text-xs p-1.5 px-3 rounded mb-5">
                <p>No card required.</p>
              </div>
              <div className="mb-5 flex-1">
                <h3 className="text-base-content/60 text-xs font-medium uppercase tracking-wide mb-2">
                  Includes
                </h3>
                <ul className="space-y-1.5">
                  {INCLUDED_FEATURES.free.map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2 text-sm text-base-content/90"
                    >
                      <span className="mt-0.5 flex-shrink-0 text-base-content">
                        ✓
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {!isStripeSubscriber ? (
                // Free user — this is their current plan
                <button
                  disabled
                  className="w-full py-3 px-4 rounded-full font-bold font-grotesk text-center bg-white/20 text-base-content/60 cursor-default"
                >
                  Current Plan
                </button>
              ) : (
                // Paid user — allow downgrade to free (cancel subscription)
                <button
                  onClick={promptDowngradeToBasic} // Changed from handleDowngradeToBasic
                  disabled={downgrading}
                  className={`w-full py-3 px-4 rounded-full font-bold font-grotesk text-center transition-colors duration-200 flex items-center justify-center gap-2 ${
                    downgrading
                      ? "bg-white/20 text-base-content/60 cursor-wait"
                      : "bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/30"
                  }`}
                >
                  {downgrading ? (
                    <span className="loading loading-spinner loading-xs" />
                  ) : (
                    "Downgrade to Basic"
                  )}
                </button>
              )}
            </div>

            {/* Legacy Stripe subscribers keep the old regional cards.
                Everyone else gets the flat USD cards in this row. */}
            {!isLegacyStripeSubscriber &&
              (["plus", "premium"] as const).map((plan) =>
                renderPeakCard(plan, peakBillingPeriod),
              )}

            {isLegacyStripeSubscriber && stripeProducts.length === 0 && (
              <p className="text-center text-base-content/60 w-full">
                No plans available right now. Please try again later.
              </p>
            )}

            {/* ── Plus & Premium Cards ── */}
            {isLegacyStripeSubscriber && [...stripeProducts]
              .sort((a, b) => {
                const order: Record<string, number> = { plus: 0, premium: 1 };
                return (
                  (order[a.type?.toLowerCase()] ?? 99) -
                  (order[b.type?.toLowerCase()] ?? 99)
                );
              })
              .map((product) => {
                const productType: "plus" | "premium" =
                  product.type?.toLowerCase() === "plus" ? "plus" : "premium";
                const style = CARD_STYLE[productType];

                const displayPrice =
                  productType === "plus"
                    ? StripeService.getPlusPriceDetailsForCountry(
                        product.plans || [],
                        validatedCountry,
                        billingPeriod,
                      )
                    : StripeService.getPriceDetailsForCountry(
                        product.plans || [],
                        validatedCountry,
                        billingPeriod,
                      );

                const price = displayPrice.price;
                const currency = displayPrice.currency;
                const pricePerMonth =
                  billingPeriod === "month"
                    ? price
                    : billingPeriod === "quarter"
                      ? price / 3
                      : price / 12;
                const pricePerWeek =
                  billingPeriod === "month"
                    ? price / 4.33
                    : billingPeriod === "quarter"
                      ? price / 13
                      : price / 52;

                const isOnThisProduct =
                  isStripeSubscriber && currentProductType === productType;
                const currentPriceId = userSubscription?.stripeDetails?.priceId;
                const currentBillingPeriod =
                  getBillingPeriodForAnyPriceId(currentPriceId);
                const isExactCurrentPlan =
                  isOnThisProduct && currentBillingPeriod === billingPeriod;

                // Legacy regional prices are closed. These cards stay visible
                // so a current subscriber can see their plan, but the only
                // subscription action left is cancel (Basic, or Cancel below).
                const ctaButtonLabel = isExactCurrentPlan
                  ? "Current Plan"
                  : "Unavailable";

                return (
                  <div
                    key={product.productId}
                    className={`${style.gradient} rounded-xl p-6 flex-1 flex flex-col relative`}
                  >
                    <h2 className="text-2xl font-bold font-geist text-base-content mb-1">
                      {style.title}
                    </h2>
                    <p className="text-sm text-base-content/70 font-geist mb-3">
                      {productType === "plus"
                        ? "Train with clarity."
                        : "Intelligent training tools."}
                    </p>
                    <div className="mb-1">
                      <span className="text-4xl font-bold text-base-content font-grotesk">
                        {formatPrice(displayPrice.price, displayPrice.currency)}
                      </span>
                      <span className="text-base-content/80 ml-2 text-sm">
                        / {formatDuration(billingPeriod)}
                      </span>
                    </div>
                    <div className="flex gap-2 mb-4 text-xs mt-2">
                      <div className="border border-base-content/20 px-4 py-2 pl-2 flex items-center font-geist rounded-md bg-base-content/5 text-base-content/60">
                        <strong>{formatPrice(pricePerMonth, currency)}</strong>
                        /month
                      </div>
                      <div className="border border-base-content/20 px-4 py-2 pl-2 flex items-center font-geist rounded-md bg-base-content/5 text-base-content/60">
                        <strong>{formatPrice(pricePerWeek, currency)}</strong>
                        /week
                      </div>
                    </div>
                    <div className="mb-5 flex-1">
                      <h3 className="text-base-content/60 text-xs font-medium uppercase tracking-wide mb-2">
                        Includes
                      </h3>
                      <ul className="space-y-1.5">
                        {INCLUDED_FEATURES[productType]?.map((feature, idx) => {
                          const isAI = AI_FEATURES.has(feature);
                          return (
                            <li
                              key={idx}
                              className={`flex items-start gap-2 text-sm text-base-content/90 ${isAI ? "rounded-md px-2 -mx-2" : ""}`}
                            >
                              <span className="mt-0.5 flex-shrink-0 text-base-content">
                                {isAI ? (
                                  <HiSparkles className="h-4 w-4" />
                                ) : (
                                  "✓"
                                )}
                              </span>
                              <span>{feature}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    <button
                      type="button"
                      className="w-full py-3 px-4 rounded-full font-bold font-grotesk flex items-center justify-center gap-2 bg-base-300/50 text-base-content/60 cursor-default"
                      disabled
                    >
                      {ctaButtonLabel}
                    </button>
                  </div>
                );
              })}
          </div>
        )}

        {/* Legacy subscribers can move onto the flat USD prices from here.
            Everyone else already has those cards in the row above. */}
        {isLegacyStripeSubscriber && (
          <div className="mb-8 bg-base-100 rounded-xl border border-base-300 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
              <div>
                <h2 className="text-xl font-bold text-base-content font-grotesk">
                  New Simplified Pricing
                </h2>
                <p className="text-sm text-base-content/70 font-geist">
                  Flat USD pricing. Switch any time — your current plan keeps running until it switches over.
                </p>
              </div>
              <div className="flex bg-base-200 rounded-full p-1 text-sm font-semibold w-fit">
                {(["month", "year"] as const).map((period) => (
                  <button
                    key={period}
                    type="button"
                    onClick={() => setPeakBillingPeriod(period)}
                    className={`px-4 py-2 rounded-full transition-colors ${
                      peakBillingPeriod === period
                        ? "bg-base-content text-base-100"
                        : "text-base-content/60 hover:text-base-content"
                    }`}
                  >
                    {period === "month" ? "Monthly" : "Annually"}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              {(["plus", "premium"] as const).map((plan) =>
                renderPeakCard(plan, peakBillingPeriod),
              )}
            </div>
          </div>
        )}

        {/* ─── Feature Comparison Table ─── */}
        <div className="bg-gradient-to-br from-primary to-blue-800  rounded-xl mb-8 overflow-x-auto text-white">
          <div className="p-6 pb-2">
            <h2 className="text-2xl">Plan Comparison</h2>
            <p className="font-geist mb-4 text-xs mt-1">
              Free to start. Built to grow with you.
            </p>
          </div>
          <div className="p-6 pt-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base-300/10">
                  <th className="text-left py-2 pl-2 text-white font-bold">
                    Feature
                  </th>
                  <th className="text-center py-2 pl-2 text-white font-bold">
                    Basic
                  </th>
                  <th className="text-center py-2 pl-2 text-white font-bold">
                    Plus
                  </th>
                  <th className="text-center py-2 pl-2 text-white font-bold">
                    Premium
                  </th>
                </tr>
              </thead>
              <tbody className="text-white">
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">Peak Readiness Score</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40 bg-base-200/30">
                  <td className="py-2 pl-2">Wearable Devices</td>
                  <td className="text-center font-bold">1</td>
                  <td className="text-white text-center font-bold">2</td>
                  <td className="text-center text-white font-bold">3</td>
                </tr>
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">Wellness Questionnaire</td>
                  <td className="text-center font-bold">✓</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40 bg-base-200/30">
                  <td className="py-2 pl-2">
                    Training Load & Exercise Logs
                  </td>
                  <td className="text-center font-bold">✓</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">Recovery, Niggles & Note logs</td>
                  <td className="text-center font-bold">✓</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40 bg-base-200/30">
                  <td className="py-2 pl-2">Heart Lung Recovery Index (HLR)</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">Load Balance</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40 bg-base-200/30">
                  <td className="py-2 pl-2">Last 7 Days Analytics</td>
                  <td className="text-center font-bold">✓</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">Last 28 Days Analytics</td>
                  <td className="text-center text-white/30  font-bold">—</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40  bg-base-200/30">
                  <td className="py-2 pl-2">
                    Quarterly & Custom
                    <br />
                    Date Range Analytics
                  </td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">
                    Activity & Exercise{" "}
                    <span className="font-geist text-lime text-xs">beta</span>
                    <br></br> Progression Analytics
                  </td>
                  <td className="text-center text-white/30  font-bold">—</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40 bg-base-200/30">
                  <td className="py-2 pl-2">
                    Planner & Scheduled Sessions
                  </td>
                  <td className="text-center font-bold">✓</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">Saved Session Templates</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">
                    AI Session Builder{" "}
                    <span className="font-geist text-lime text-xs">beta</span>
                    <br></br>Voice, CSV or image to log
                  </td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40 bg-base-200/30">
                  <td className="py-2 pl-2">Export Data to CSV</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-center text-white font-bold">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">Exercise Library</td>
                  <td className="text-center font-bold">✓</td>
                  <td className="text-white font-bold text-center">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40  bg-base-200/30">
                  <td className="py-2 pl-2">The Wizard AI</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-white font-bold text-center">✓</td>
                </tr>
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">Personalised AI Insights</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40  bg-base-200/30">
                  <td className="py-2 pl-2">
                    Priority Access to Elite Features
                  </td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
                <tr className="border-b border-base-200/40">
                  <td className="py-2 pl-2">Custom Dashboard Background</td>
                  <td className="text-white/30 text-center">—</td>
                  <td className="text-center text-white font-bold">✓</td>
                  <td className="text-center text-white font-bold">✓</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Stripe Subscription Actions — UNCHANGED */}
        {userSubscription &&
          userSubscription?.subscriptionVia === "stripe" &&
          stripeProducts.length > 0 && (
            <div className="mt-6">
              <StripeSubscriptionActions
                uid={currentUser?.uid ?? null}
                subscription={userSubscription}
                onSubscriptionChange={async () => {
                  if (currentUser?.uid) {
                    refreshSubscription();
                  }
                }}
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default SubscriptionSettings;
